(() => {
  'use strict';

  if (window.freeToteGiftInitialized) return;
  window.freeToteGiftInitialized = true;

  const configNode = document.getElementById('FreeToteGiftConfig');
  let config = {
    enabled: false,
    threshold: 150,
    variantId: null,
    productId: null,
    variantPrice: 0,
    discountCode: 'Free Tote Bag',
    title: 'tote bag'
  };

  if (configNode) {
    try {
      config = { ...config, ...JSON.parse(configNode.textContent || '{}') };
    } catch (error) {
      console.error('Free tote config could not be read.', error);
    }
  }

  if (!config.enabled || !config.variantId) return;

  const GIFT_PROPERTY = '_gift_tote';
  const INTERNAL_HEADER = 'X-Envelope-Limit-Internal';
  const variantId = String(config.variantId);
  const discountCode = String(config.discountCode || 'Free Tote Bag').trim();

  let syncing = false;
  let queued = false;
  let noticeTimer = 0;
  let lastDiscountAttempt = '';
  let latestCart = null;
  let paintQueued = false;

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const thresholdCents = () => {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Math.round(Number(config.threshold || 150) * 100 * (Number.isFinite(rate) && rate > 0 ? rate : 1));
  };

  const formatMoney = (cents) => {
    if (typeof window.Shopify?.formatMoney === 'function' && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }

    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const request = async (path, body) => {
    const response = await fetch(`${rootUrl()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [INTERNAL_HEADER]: '1'
      },
      body: JSON.stringify(body)
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'The cart could not be updated.');
    }

    return payload;
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: {
        Accept: 'application/json',
        [INTERNAL_HEADER]: '1'
      }
    });

    if (!response.ok) {
      throw new Error('The cart could not be read.');
    }

    return response.json();
  };

  const isGiftLine = (item) => String(item.variant_id) === variantId;

  const qualifyingTotal = (cart) =>
    (cart.items || []).reduce((total, item) => {
      if (isGiftLine(item)) return total;
      return total + Number(item.final_line_price ?? item.line_price ?? 0);
    }, 0);

  const showNotice = (message) => {
    let notice = document.querySelector('[data-free-tote-notice]');

    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'free-tote-notice';
      notice.setAttribute('data-free-tote-notice', '');
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      document.body.appendChild(notice);
    }

    notice.textContent = message;
    notice.classList.add('is-visible');
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => {
      notice.classList.remove('is-visible');
    }, 4200);
  };

  const updateBanner = (cart) => {
    const banner = document.querySelector('[data-free-tote-banner]');
    if (!banner) return;

    const remaining = thresholdCents() - qualifyingTotal(cart);
    const giftAdded = (cart.items || []).some(isGiftLine);

    if (remaining > 0) {
      banner.hidden = false;
      banner.textContent = `Spend ${formatMoney(remaining)} more and a free tote bag will be added to your cart.`;
      return;
    }

    banner.hidden = false;
    banner.textContent = giftAdded
      ? 'Your free tote bag has been added to your cart.'
      : 'Your cart qualifies for a free tote bag.';
  };

  const updateCartCount = (count) => {
    if (typeof count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count < 1;
    });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const countElement =
        bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');

      if (countElement) countElement.textContent = count;
      bubble.hidden = count < 1;
      bubble.classList.toggle('hidden', count < 1);
    });
  };

  const refreshCartSection = async () => {
    const root = document.querySelector('[data-ajax-main-cart]');
    const sectionId = root?.dataset.sectionId;
    if (!root || !sectionId) return;

    const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {
      headers: { [INTERNAL_HEADER]: '1' }
    });

    if (!response.ok) return;

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const updated = parsed.querySelector('[data-ajax-main-cart]');

    if (updated) root.replaceWith(updated);
  };

  const giftCodeEntry = (cart, code = discountCode) =>
    (cart.discount_codes || []).find(
      (entry) => entry.code?.toLowerCase() === String(code).toLowerCase()
    );

  const giftLineCharge = (cart) =>
    (cart.items || [])
      .filter(isGiftLine)
      .reduce((total, item) => total + Number(item.final_line_price ?? item.line_price ?? item.price ?? 0), 0);

  const discountCandidates = () => {
    const candidates = [];
    const addCandidate = (code) => {
      const value = String(code || '').trim();
      if (!value) return;
      if (candidates.some((existing) => existing.toLowerCase() === value.toLowerCase())) return;
      candidates.push(value);
    };

    addCandidate(discountCode);
    addCandidate(discountCode.replace(/\s+/g, ''));
    addCandidate(discountCode.replace(/\s+/g, '').toUpperCase());
    return candidates;
  };

  const applyDiscountCandidate = async (code) => {
    await fetch(`${rootUrl()}discount/${encodeURIComponent(code)}`, {
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { [INTERNAL_HEADER]: '1' }
    }).catch(() => null);

    try {
      return await request('cart/update.js', { discount: code });
    } catch (error) {
      return getCart();
    }
  };

  const syncDiscountCode = async (cart, qualified) => {
    if (!discountCode || Number(config.variantPrice) === 0) return cart;

    const giftInCart = (cart.items || []).some(isGiftLine);
    const shouldApply = qualified && giftInCart;
    const appliedCode = discountCandidates().find((code) => giftCodeEntry(cart, code));

    if (!shouldApply) {
      if (!appliedCode) return cart;

      const remaining = (cart.discount_codes || [])
        .map((entry) => entry.code)
        .filter((code) => code && code.toLowerCase() !== appliedCode.toLowerCase());

      try {
        return await request('cart/update.js', { discount: remaining.join(',') });
      } catch (error) {
        return cart;
      }
    }

    if (giftLineCharge(cart) === 0) return cart;

    const attemptKey = `${cart.token}:${qualifyingTotal(cart)}:${giftLineCharge(cart)}`;
    if (lastDiscountAttempt === attemptKey) return cart;
    lastDiscountAttempt = attemptKey;

    let nextCart = cart;

    for (const code of discountCandidates()) {
      nextCart = await applyDiscountCandidate(code);
      if (giftLineCharge(nextCart) === 0 || giftCodeEntry(nextCart, code)?.applicable) {
        return nextCart;
      }
    }

    const remaining = (nextCart.discount_codes || [])
      .map((entry) => entry.code)
      .filter((code) => code && !discountCandidates().some((candidate) => candidate.toLowerCase() === code.toLowerCase()));

    if (remaining.length === (nextCart.discount_codes || []).length) return nextCart;

    try {
      return await request('cart/update.js', { discount: remaining.join(',') });
    } catch (error) {
      return nextCart;
    }
  };

  const syncGift = async () => {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;

    try {
      let cart = await getCart();
      let changed = false;
      const qualified = qualifyingTotal(cart) >= thresholdCents();
      const giftLines = (cart.items || []).filter(isGiftLine);

      if (qualified && giftLines.length === 0) {
        await request('cart/add.js', {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'true'
              }
            }
          ]
        });
        changed = true;
        showNotice('Your free tote bag has been added to your cart.');
      } else if (qualified && giftLines.some((item) => item.quantity !== 1 || giftLines.length > 1)) {
        const updates = {};
        giftLines.forEach((item, index) => {
          updates[item.key] = index === 0 ? 1 : 0;
        });
        await request('cart/update.js', { updates });
        changed = true;
      } else if (!qualified && giftLines.length > 0) {
        const updates = {};
        giftLines.forEach((item) => {
          updates[item.key] = 0;
        });
        await request('cart/update.js', { updates });
        changed = true;
      }

      if (changed) cart = await getCart();

      latestCart = cart;
      updateBanner(cart);
      updateCartCount(cart.item_count);
      queueMiniCartPaint();

      if (changed) {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart, source: 'free-tote-gift' }
          })
        );
        refreshCartSection();
      }

      if (Number(config.variantPrice) !== 0) {
        const nextCart = await syncDiscountCode(cart, qualifyingTotal(cart) >= thresholdCents());
        if (nextCart?.items) {
          latestCart = nextCart;
          updateBanner(nextCart);
          updateCartCount(nextCart.item_count);
          queueMiniCartPaint();
        }
      }
    } catch (error) {
      console.error('Free tote gift could not be updated.', error);
    } finally {
      syncing = false;

      if (queued) {
        queued = false;
        window.setTimeout(syncGift, 0);
      }
    }
  };

  const toteTitle = String(config.title || 'tote bag').trim().toLowerCase();

  const isMiniCartTote = (node) => {
    const title = node.querySelector('[data-aid="minicart-product-name"]')?.textContent || '';
    const href = node.querySelector('a')?.getAttribute('href') || '';
    return title.trim().toLowerCase() === toteTitle || href.includes('teapigs-tote-bag');
  };

  const paintMiniCart = () => {
    if (!latestCart) return;

    const giftItems = (latestCart.items || []).filter(isGiftLine);
    const toteNodes = [...document.querySelectorAll('.minicart-product')].filter(isMiniCartTote);

    if (giftItems.length && toteNodes.length === giftItems.length) {
      toteNodes.forEach((node) => {
        const price = node.querySelector('.product-price');
        if (price && price.textContent.trim() !== 'Free') price.textContent = 'Free';
      });
    }

    const subtotal = document.querySelector('.minicart-footer-subtotal-value');
    const charge = giftLineCharge(latestCart);
    if (!subtotal || charge <= 0) return;

    const displayTotal = Math.max(0, Number(latestCart.total_price || 0) - charge);
    const formatted = formatMoney(displayTotal);
    if (formatted && subtotal.textContent.trim() !== formatted.trim()) {
      subtotal.textContent = formatted;
    }
  };

  const queueMiniCartPaint = () => {
    if (paintQueued) return;
    paintQueued = true;
    window.requestAnimationFrame(() => {
      paintQueued = false;
      paintMiniCart();
    });
  };

  const scheduleSync = () => {
    window.clearTimeout(scheduleSync.timer);
    scheduleSync.timer = window.setTimeout(syncGift, 0);
  };

  const cartMutationPattern = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    return originalFetch(input, init).then((response) => {
      const url = typeof input === 'string' ? input : input?.url || '';
      const headers = init?.headers;
      const internal =
        (typeof headers?.get === 'function' && headers.get(INTERNAL_HEADER) === '1') ||
        headers?.[INTERNAL_HEADER] === '1';

      if (!internal && cartMutationPattern.test(String(url))) scheduleSync();
      return response;
    });
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._freeToteUrl = url;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (String(name).toLowerCase() === INTERNAL_HEADER.toLowerCase() && String(value) === '1') {
      this._freeToteInternal = true;
    }
    return originalSetRequestHeader.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    this.addEventListener('load', () => {
      if (this._freeToteInternal) return;
      if (cartMutationPattern.test(String(this._freeToteUrl || ''))) scheduleSync();
    });
    return originalSend.apply(this, arguments);
  };

  ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.detail?.source === 'free-tote-gift') return;
      scheduleSync();
    });
  });

  const miniCartRoot = document.querySelector('[data-app-layer-portal="minicart"]') || document.body;
  const miniCartObserver = new MutationObserver(() => queueMiniCartPaint());
  miniCartObserver.observe(miniCartRoot, { childList: true, subtree: true, characterData: true });

  window.addEventListener('focus', scheduleSync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleSync();
  });

  syncGift();
})();
